import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import type { Language, TranslationKey, Translations } from './translations/keys'
import { en } from './translations/en'
import { fi } from './translations/fi'

const translations: Record<string, Translations> = { en, fi }

/**
 * Options for the `t()` translation function.
 */
interface TOptions {
  /**
   * Value to return when the translation key is missing.
   *
   * ⚠️ NOT a safety fallback. Do not use this to avoid adding translations.
   * Missing keys should always be added to both `en.ts` and `fi.ts`.
   * Reserve this for genuinely dynamic or optional keys (e.g. server-provided labels).
   */
  missingValue?: string
}

interface I18nContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: TranslationKey, options?: TOptions) => string
}

const I18nContext = createContext<I18nContextType | undefined>(undefined)

interface I18nProviderProps {
  children: ReactNode
  defaultLanguage?: Language
  /** Called once on mount — return a persisted language code or null */
  loadLanguage?: () => Promise<Language | null>
  /** Called whenever the language changes — persist it however you like */
  saveLanguage?: (lang: Language) => void | Promise<void>
}

export function I18nProvider({
  children,
  defaultLanguage = 'fi',
  loadLanguage,
  saveLanguage,
}: I18nProviderProps) {
  const [language, setLanguageState] = useState<Language>(defaultLanguage)

  useEffect(() => {
    if (!loadLanguage) return
    loadLanguage().then((lang) => {
      if (lang) setLanguageState(lang)
    })
  }, [])

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang)
    saveLanguage?.(lang)
  }, [saveLanguage])

  const t = useCallback((key: TranslationKey, options?: TOptions): string => {
    return translations[language]?.[key] || options?.missingValue || key
  }, [language])

  return React.createElement(
    I18nContext.Provider,
    { value: { language, setLanguage, t } },
    children,
  )
}

export function useLanguage() {
  const context = useContext(I18nContext)
  if (!context) throw new Error('useLanguage must be used within I18nProvider')
  return context
}